import ActionBase, { ACTION_TYPES } from './ActionBase.js';
import Store from '../../Store.js';

/**
 * @module Core/data/stm/action/InsertAction
 */

/**
 * Action to record the fact of models inserting into a store.
 * @extends Core/data/stm/action/ActionBase
 */
export default class InsertAction extends ActionBase {
    static $name = 'InsertAction';

    static configurable = {
        /**
         * Reference to a store that models have been inserted into.
         *
         * @prp {Core.data.Store}
         * @readonly
         * @default
         */
        store : undefined,

        /**
         * List of models inserted into the store.
         *
         * @prp {Core.data.Model[]}
         * @readonly
         * @default
         */
        modelList : undefined,

        /**
         * Index the models have been inserted at.
         *
         * @prp {Number}
         * @readonly
         * @default
         */
        insertIndex : undefined,

        /**
         * Models move context (if models has been moved), if any.
         * Map this {@link Core/data/Model} instances as keys and their
         * previous index as values
         *
         * @prp {Map}
         * @readonly
         * @default
         */
        context : undefined,

        /**
         * Flag showing if undo/redo should be done silently i.e. with events suppressed
         *
         * @prp {Boolean}
         * @readonly
         * @default
         */
        silent : false,

        reportUndoneRemoval : true
    };

    get type() {
        return ACTION_TYPES.INSERT;
    }

    //<debug>
    afterConfig() {
        super.afterConfig();

        console.assert(
            this.store instanceof Store &&
            Array.isArray(this.modelList) &&
            this.modelList.length &&
            this.modelList.every(m => m.isModel) &&
            this.insertIndex !== undefined &&
            this.context instanceof Map,
            "Can't create action, bad configuration!"
        );
    }
    //</debug>

    changeStore(store) {
        //<debug>
        console.assert(
            !this._store && store && store instanceof Store,
            "Can't set store, store is required and can be set only once!"
        );
        //</debug>

        return store;
    }

    changeModelList(list) {
        //<debug>
        console.assert(
            !this._modelList &&
            Array.isArray(list) &&
            list.length &&
            list.every(m => m.isModel),
            "Can't set model list, model list is required, it should be array of Models and it can be set only once!"
        );
        //</debug>

        return list.slice(0);
    }

    changeInsertIndex(index) {
        //<debug>
        console.assert(
            this._insertIndex === undefined && index !== undefined,
            "Can't set insert index, the value is required and can be set only once!"
        );
        //</debug>

        return index;
    }

    changeContext(context) {
        //<debug>
        console.assert(
            !this._context &&
            context instanceof Map &&
            Array.from(context.entries()).every(([k, v]) => k.isModel && v !== undefined),
            "Can't set move context, context is required, it should be Map with keys set to Model instances and numeric values, and it can be set only once!"
        );
        //</debug>

        return context;
    }

    undo() {
        const { store, modelList, context, silent, reportUndoneRemoval } = this;

        // Let's sort models by index such that models with lesser index
        // were inserted back first, thus making valid index of models following.

        modelList.sort((lhs, rhs) => {
            const
                lhsIndex = context.get(lhs),
                rhsIndex = context.get(rhs);

            return lhsIndex !== undefined && rhsIndex !== undefined ? lhsIndex - rhsIndex : 0;
        });

        modelList.forEach(m => {
            const index = context.get(m);

            // Flag the inserted record that we undo to skip adding it to "store.removed"
            m._undoingInsertion = reportUndoneRemoval;

            if (index !== undefined) {
                // Insert at previous index
                store.insert(index, m, silent);
            }
            else {
                // Just remove
                store.remove(m, silent);
            }

            m._undoingInsertion = false;
        });
    }

    redo() {
        const me = this;
        me.store.insert(me.insertIndex, me.modelList, me.silent);
    }
}
